import React, { useEffect, useState, useCallback } from "react";
import { Smartphone, Download, X, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "ot_maquila_pwa_dismissed_at";
const DISMISS_DAYS = 7; // Do not re-prompt for 7 days after user closes

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari
  if (window.navigator.standalone === true) return true;
  // Modern browsers
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  const isMacTouch = ua.includes("Mac") && "ontouchend" in document;
  return isIOSDevice || isMacTouch;
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed
    if (wasRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Small delay so we don't block first paint
      setTimeout(() => setVisible(true), 1200);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari does not fire beforeinstallprompt — show help modal
    if (isIOS() && !isStandalone()) {
      setTimeout(() => {
        setIosHelp(true);
        setVisible(true);
      }, 1500);
    }

    const installed = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        setVisible(false);
      }
    } catch {
      // No-op
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwa-install-title"
      data-testid="pwa-install-prompt"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={handleDismiss}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="pwa-install-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md rounded-2xl border border-primary/30 bg-card shadow-2xl shadow-primary/10 p-5 sm:p-6 animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300"
        data-testid="pwa-install-card"
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          aria-label="Cerrar"
          data-testid="pwa-install-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header row */}
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-14 w-14 rounded-2xl bg-primary/15 border border-primary/40 flex items-center justify-center">
            <Smartphone className="h-7 w-7 text-primary" aria-hidden />
          </div>
          <div className="pt-1 pr-6">
            <h2
              id="pwa-install-title"
              className="text-lg font-semibold tracking-tight text-foreground"
              data-testid="pwa-install-title"
            >
              Instalar OT Maquila
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Accede rápido desde tu pantalla de inicio.
            </p>
          </div>
        </div>

        {/* Body */}
        {iosHelp ? (
          <div className="mt-5 space-y-3">
            <div className="rounded-lg bg-muted/40 border border-border p-3.5 text-sm">
              <p className="text-foreground font-medium mb-2">
                En iPhone / iPad (Safari):
              </p>
              <ol className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-[11px] font-semibold shrink-0">
                    1
                  </span>
                  <span>Toca el botón</span>
                  <Share className="h-4 w-4 text-primary" aria-hidden />
                  <span className="font-medium text-foreground">Compartir</span>
                  <span>abajo.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-[11px] font-semibold shrink-0 mt-0.5">
                    2
                  </span>
                  <span className="flex items-center gap-1 flex-wrap">
                    <span>Selecciona</span>
                    <PlusSquare className="h-4 w-4 text-primary" aria-hidden />
                    <span className="font-medium text-foreground">Añadir a pantalla de inicio</span>.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-[11px] font-semibold shrink-0 mt-0.5">
                    3
                  </span>
                  <span>Confirma con <span className="font-medium text-foreground">Añadir</span>.</span>
                </li>
              </ol>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleDismiss}
              className="w-full"
              data-testid="pwa-install-ok-button"
            >
              Entendido
            </Button>
          </div>
        ) : (
          <div className="mt-5">
            <ul className="space-y-2 text-sm text-muted-foreground mb-5">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Se abre a pantalla completa, como una app nativa.
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Un solo tap para llegar a tu Dashboard.
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Sin publicidad, sin tiendas: 100% empresa.
              </li>
            </ul>

            <Button
              type="button"
              onClick={handleInstall}
              disabled={installing || !deferredPrompt}
              className="w-full h-12 text-base font-semibold shadow-lg shadow-primary/20"
              data-testid="pwa-install-button"
            >
              <Download className="h-5 w-5 mr-2" />
              {installing ? "Instalando..." : "Instalar App"}
            </Button>

            <button
              type="button"
              onClick={handleDismiss}
              className="w-full text-xs text-muted-foreground mt-3 hover:text-foreground transition-colors"
              data-testid="pwa-install-later"
            >
              Ahora no
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default InstallPWAPrompt;
